import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { SessionTimeoutService } from './core/services/session-timeout.service';
import { SessionTimeoutModalComponent } from './shared/components/session-timeout-modal/session-timeout-modal.component';
import { ToastContainerComponent } from './shared/components/toast-container/toast-container.component';
import { FormDeactivateService } from './core/services/form-deactivate.service';
import { MyCMIntegrationService } from './core/services/myCM-integration-store';
import { ToastService } from './core/services/toast.service';
import { debugLog, debugWarn } from './core/utils/debug-logger';
import { EthelHostService } from './features/ethel/services/ethel-host.service';
import { EthelHostComponent } from './features/ethel/components/ethel-host/ethel-host.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SessionTimeoutModalComponent, ToastContainerComponent, EthelHostComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  /** Injecting the service here initialises it as a singleton and starts the inactivity effect. */
  readonly sessionTimeoutService = inject(SessionTimeoutService);
  /**
   * ADO 149676 (U6). Pattern A (design.md §7.1) — the `@if` in `app.html` reads a
   * root-service signal held as a public field, matching `sessionTimeoutService` above.
   * Nothing exists in the DOM while `mounted()` is false (AC-024/AC-U6-07/AC-423).
   */
  readonly ethelHost = inject(EthelHostService);
  private readonly router = inject(Router);
  protected readonly formDeactivate = inject(FormDeactivateService);
  private readonly myCMService = inject(MyCMIntegrationService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * True while a route navigation (including resolver phase) is in progress.
   * Starts as TRUE: the app always begins with an active navigation on initial load.
   * Drives the full-page loader so the blank-page gap during resolver phase is never visible.
   */
  protected readonly isNavigating = signal(true);
  private _recoveredFromNotFound = false;

  /** Best-effort top-level route bucket for debugging route matching decisions. */
  private getTopLevelRouteBucket(url: string): string {
    const cleanUrl = url.split('?')[0].split('#')[0] || '';
    const topLevel = cleanUrl.replace(/^\//, '').split('/')[0] || '(root)';
    const routePath = this.router.config.find((r) => {
      const path = r.path ?? '';
      if (path === '**') return false;
      if (path === '') return topLevel === '(root)';
      const routeTopLevel = path.split('/')[0];
      return routeTopLevel === topLevel;
    })?.path;
    return routePath ?? '(catch-all/**)';
  }

  ngOnInit(): void {
    const locationPath = `${globalThis.location.pathname}${globalThis.location.search}`;
    debugLog('EP Router', 'InitialUrlSnapshot', {
      routerUrl: this.router.url,
      locationPath,
      initialBucket: this.getTopLevelRouteBucket(this.router.url || locationPath),
      containsAdminDashboard: locationPath.includes('/admin-dashboard'),
    });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
      if (event instanceof NavigationStart) {
        debugLog('EP Router', 'NavigationStart', {
          id: event.id,
          url: event.url,
          topLevelRouteBucket: this.getTopLevelRouteBucket(event.url),
          href: globalThis.location.href,
        });
        // Don't show the loader when navigating away from a guarded form route —
        // the deactivation guard will handle the UX (dirty check + modal).
        // Showing the loader would cover the form and make the modal invisible.
        if (!this.formDeactivate.hasHandler) {
          this.isNavigating.set(true);
        }
      } else if (event instanceof NavigationCancel) {
        debugLog('EP Router', 'NavigationCancel', {
          id: event.id,
          url: event.url,
          reason: event.reason,
          code: event.code,
          href: globalThis.location.href,
        });
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationError
      ) {
        if (event instanceof NavigationEnd) {
          debugLog('EP Router', 'NavigationEnd', {
            id: event.id,
            url: event.url,
            urlAfterRedirects: event.urlAfterRedirects,
            startBucket: this.getTopLevelRouteBucket(event.url),
            finalBucket: this.getTopLevelRouteBucket(event.urlAfterRedirects),
            href: globalThis.location.href,
          });
        }
        if (event instanceof NavigationError) {
          debugWarn('EP Router', 'NavigationError', {
            id: event.id,
            url: event.url,
            error: event.error as string,
            href: globalThis.location.href,
          });
        }
        this.isNavigating.set(false);
      }
    });
    // Safety: if navigation already completed before ngOnInit ran, clear immediately.
    if (this.router.getCurrentNavigation() === null) { // NOSONAR
      this.isNavigating.set(false);
    }

    // Offline/online connectivity detection.
    fromEvent(globalThis.window, 'offline')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toastService.show(
          'No internet connection. Some features may not be available.',
          'error',
          30_000,
        );
      });

    fromEvent(globalThis.window, 'online')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toastService.show('Connection restored.', 'success');
      });

    // Recovery path for iframe timing races:
    // If routing has already fallen through to /not-found, but MyCM config/token
    // arrives later, recover by navigating to the admin portal configuration route.
    if (globalThis.self !== globalThis.top) {
      this.myCMService.myCMConfig$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((config) => {
        if (this._recoveredFromNotFound) {
          return;
        }
        if (!config?.token) {
          return;
        }
        if (!this.router.url.startsWith('/not-found')) {
          return;
        }

        const queryCid = new URLSearchParams(globalThis.location.search).get('cid');
        const customerId = config.customerId || queryCid || localStorage.getItem('customerId') || '';
        if (!customerId) {
          debugWarn('EP Recovery', 'token arrived on /not-found but no customerId available');
          return;
        }

        this._recoveredFromNotFound = true;
        debugWarn('EP Recovery', 'token arrived after not-found, redirecting to portal configuration', {
          customerId,
          currentUrl: this.router.url,
          href: globalThis.location.href,
        });

        void this.router.navigate(
          ['/', 'admin-dashboard', 'customers', customerId, 'portal-configuration'],
          { replaceUrl: true },
        );
      });
    }
  }
}
